/**
 * decodeYoutubeTitle
 * ==================
 * YouTube Data API returns search titles with HTML-encoded entities
 * (`&amp;`, `&#39;`, `&quot;`, etc.) so the raw string can't be displayed
 * directly. Previous code used `dangerouslySetInnerHTML` to let the
 * browser decode them, but that opens an XSS hole if a video title
 * contains `<script>` or similar (titles aren't sanitised by YouTube).
 *
 * This helper decodes the safe subset of entities to plain text. The
 * result is always rendered through React's text-content path, so any
 * stray `<` / `>` characters are correctly escaped at render time.
 *
 * Why not DOMPurify? — we only need to display the title as plain text,
 * not preserve any markup. Adding a 90KB dep for this one use case is
 * over-kill.
 */
export function decodeYoutubeTitle(input) {
  if (input == null) return '';
  const s = String(input);
  if (!s) return '';
  // Use the browser's textarea trick to decode named + numeric entities
  // exactly once, then strip any HTML tags that survive (defence in depth:
  // a malicious title shouldn't be able to inject anchor / iframe markup
  // even if YouTube ever started returning it).
  //
  // SECURITY NOTE: although `.innerHTML =` appears below, the value we
  // read back is `.value` (textContent of the textarea) — never the
  // parsed HTML — so this is the standard SAFE decode pattern. The
  // strip-tags regex on the next line is belt-and-braces. Do NOT rewrite
  // this to use `.innerHTML` for the read, which would re-introduce XSS.
  const ta = typeof document !== 'undefined' ? document.createElement('textarea') : null;
  let decoded = s;
  if (ta) {
    ta.innerHTML = s; // safe: we read .value (decoded text), never .innerHTML
    decoded = ta.value;
  }
  // Plain-text only. Strip any remaining tags.
  return decoded.replace(/<[^>]*>/g, '');
}
